import { useConvexConfig } from "@/hooks/useConvexConfig";
import { useConvexRedraw } from "@/hooks/useConvexRedraw";
import { useGameStore } from "@/store/gameStore";
import { LucideIcon, Share, Shuffle, Undo2, Users } from "lucide-react";

interface MenuFaceProps {
  /** Turns the card into sharing. */
  onShare: () => void;
  /** Opens court setup. */
  onSetup: () => void;
  close: () => void;
}

/**
 * The game menu, shown when the court card grows. Big, game-style buttons:
 * Share Cards on top, Start Over and Redraw side by side during a round,
 * and Court Setup at the bottom.
 */
export const MenuFace: React.FC<MenuFaceProps> = ({
  onShare,
  onSetup,
  close,
}) => {
  const isPlaying = useGameStore((state) => state.isPlaying());
  const { redrawCard } = useConvexRedraw();
  const { configGameWithSync } = useConvexConfig();

  const startOver = () => {
    close();
    const { game } = useGameStore.getState();
    configGameWithSync({
      courts: game.courts,
      players: game.players,
      perCourt: game.perCourt,
      shortCourt: game.shortCourt,
    });
  };

  // Swaps the card on screen for one nobody has drawn yet. When there's a
  // card underneath, the old card flies off before the new one flies in.
  const redraw = async () => {
    close();
    const { localDrawnCards, drawnIndex, navigateBack, navigateNext } =
      useGameStore.getState();
    if (drawnIndex <= 0) {
      await redrawCard();
      return;
    }
    const deckIndex = localDrawnCards[drawnIndex];
    navigateBack();
    await new Promise((resolve) => setTimeout(resolve, 150));
    await redrawCard(deckIndex);
    navigateNext();
  };

  return (
    <div className="flex flex-col">
      <Row title="Share Cards" Icon={Share} onClick={onShare} />
      {isPlaying ? (
        <>
          <Divider />
          {/* Fixed height, so the line between them doesn't stretch the row. */}
          <div className="flex h-[116px]">
            <Tile title="Start Over" Icon={Undo2} onClick={startOver} />
            <div className="my-4 w-px bg-current/20" />
            <Tile title="Redraw" Icon={Shuffle} onClick={redraw} />
          </div>
        </>
      ) : null}
      <Divider />
      <Row title="Court Setup" Icon={Users} onClick={onSetup} />
    </div>
  );
};

const press =
  "transition-[scale,opacity] duration-150 select-none active:scale-95 active:opacity-70";

const Divider = () => <div className="mx-5 h-px bg-current/20" />;

/** A full-width option: icon beside its name. */
const Row: React.FC<{
  title: string;
  Icon: LucideIcon;
  onClick: () => void;
}> = ({ title, Icon, onClick }) => (
  <button
    type="button"
    className={`flex min-h-[72px] w-full items-center justify-center gap-3.5 text-[26px] font-extrabold uppercase ${press}`}
    onClick={onClick}
  >
    <Icon className="size-[26px] shrink-0" strokeWidth={3} aria-hidden />
    {title}
  </button>
);

/** A half-width option: large icon above its name. */
const Tile: React.FC<{
  title: string;
  Icon: LucideIcon;
  onClick: () => void;
}> = ({ title, Icon, onClick }) => (
  <button
    type="button"
    className={`flex flex-1 flex-col items-center justify-center gap-2 text-xl font-extrabold uppercase ${press}`}
    onClick={onClick}
  >
    <Icon className="size-10" strokeWidth={3} aria-hidden />
    {title}
  </button>
);
